/**
 * SavedAddresses.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Profile section (/my_profile/saved_addresses) for managing the customer's
 * delivery address book — full CRUD: add, edit, delete, set primary. Reuses the
 * shared `useSavedAddresses` hook + `AddressAutocomplete`, the same primitives
 * the checkout SavedAddressPicker uses, so the address book is one source of
 * truth across the app.
 */

import React, { useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import StarIcon from "@material-ui/icons/Star";
import EditIcon from "@material-ui/icons/EditOutlined";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import { BounceLoader } from "react-spinners";

import PageLayout from "../PageLayout/PageLayout";
import AddressAutocomplete from "../Address/AddressAutocomplete";
import useSavedAddresses from "../Address/useSavedAddresses";
import { isValidDeliveryPhone, savedAddressLatLng } from "../../utils/shippingValidation";

const ACCENT = "#03660c";

const Bar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
`;

const AddBtn = styled.button`
  border: 1px solid ${ACCENT};
  background: ${ACCENT};
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  padding: 9px 16px;
  cursor: pointer;
  font-family: "Poppins", sans-serif;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Card = styled.div`
  border: 1.5px solid ${(p) => (p.$primary ? ACCENT : "#e3e7e8")};
  background: ${(p) => (p.$primary ? "#f1f8f1" : "#fff")};
  border-radius: 10px;
  padding: 12px 14px;
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Label = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #14282d;
`;

const PrimaryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #fff;
  background: ${ACCENT};
  border-radius: 4px;
  padding: 2px 6px;
`;

const InvalidBadge = styled(PrimaryBadge)`
  background: #a76700;
`;

const AddressText = styled.div`
  font-size: 13px;
  color: #51626a;
  line-height: 1.4;
  margin-top: 4px;
`;

const Phone = styled.div`
  font-size: 12px;
  color: #7a8a90;
  margin-top: 2px;
`;

const Actions = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 8px;
`;

const ActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: none;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  color: ${(p) => (p.$danger ? "#c0392b" : ACCENT)};
  &:disabled {
    color: #b6bfc2;
    cursor: not-allowed;
  }
  &:hover:not(:disabled) {
    text-decoration: underline;
  }
`;

const FormCard = styled.div`
  border: 1px dashed #c9d2d4;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 12px;
  background: #fafbfb;
`;

const Field = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 9px 10px;
  margin: 0 0 8px 0;
  border: 1px solid #d6dcde;
  border-radius: 8px;
  font-size: 13px;
  outline: none;
  &:focus {
    border-color: ${ACCENT};
  }
`;

const CheckRow = styled.label`
  display: flex;
  /* Force row: a global "label { display:flex; flex-direction:column }" leaks
     app-wide from the card-form CSS (CardPinForm.css). Without this, the label
     inherits column → the checkbox stacks above the text and align-items:center
     centers the pair. */
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  width: 100%;
  font-size: 12px;
  color: #51626a;
  margin: 2px 0 10px 0;
  cursor: pointer;

  /* And a global "input { width: 100% }" (same source) stretches the checkbox —
     pin it to a fixed size so the row stays a tight, left-aligned pair. */
  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
    margin: 0;
    padding: 0;
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 8px;
`;

const PrimaryBtn = styled.button`
  border: 1px solid ${ACCENT};
  background: ${ACCENT};
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  padding: 9px 16px;
  cursor: pointer;
  &:disabled {
    background: #b6bfc2;
    border-color: #b6bfc2;
    cursor: not-allowed;
  }
`;

const GhostBtn = styled.button`
  border: 1px solid #d6dcde;
  background: #fff;
  color: #51626a;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  padding: 9px 16px;
  cursor: pointer;
`;

const Muted = styled.div`
  font-size: 13px;
  color: #7a8a90;
  padding: 10px 0;
`;

const ErrorText = styled.div`
  font-size: 13px;
  color: #c0392b;
  padding: 8px 0;
`;

const EMPTY = { label: "", address: "", recipientPhone: "", isPrimary: false };

const SavedAddresses = () => {
  const { addresses, loading, error, create, update, remove, makePrimary } =
    useSavedAddresses();

  // editing: null = closed, "new" = adding, "<id>" = editing that address.
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [coords, setCoords] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const openAdd = () => {
    setForm(EMPTY);
    setCoords(null);
    setEditing("new");
  };

  const openEdit = (a) => {
    setForm({
      label: a.label || "",
      address: a.address || a.description || "",
      recipientPhone: a.recipientPhone || "",
      isPrimary: !!a.isPrimary,
    });
    setCoords(savedAddressLatLng(a));
    setEditing(a._id);
  };

  const closeForm = () => {
    setEditing(null);
    setForm(EMPTY);
    setCoords(null);
  };

  const canSave =
    form.label.trim() &&
    form.address.trim() &&
    isValidDeliveryPhone(form.recipientPhone) &&
    Boolean(coords);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        label: form.label.trim(),
        address: form.address.trim(),
        description: form.address.trim(),
        latLng: coords,
        recipientPhone: form.recipientPhone.trim(),
        isPrimary: form.isPrimary,
      };
      if (editing === "new") {
        await create(payload);
        toast.success("Address added.");
      } else {
        await update(editing, payload);
        toast.success("Address updated.");
      }
      closeForm();
    } catch (e) {
      toast.error("Could not save the address. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete "${a.label || "this address"}"?`)) return;
    setBusyId(a._id);
    try {
      await remove(a._id);
      toast.success("Address deleted.");
    } catch (e) {
      toast.error("Could not delete the address.");
    } finally {
      setBusyId(null);
    }
  };

  const handlePrimary = async (a) => {
    setBusyId(a._id);
    try {
      await makePrimary(a._id);
    } catch (e) {
      toast.error("Could not set the primary address.");
    } finally {
      setBusyId(null);
    }
  };

  const renderForm = () => (
    <FormCard>
      <Field
        placeholder="Label (e.g. Home, Office)"
        value={form.label}
        onChange={(e) => setForm({ ...form, label: e.target.value })}
      />
      <AddressAutocomplete
        value={form.address}
        onChange={(v) => {
          setForm((f) => ({ ...f, address: v }));
          setCoords(null); // require a re-resolve when the text changes
        }}
        onResolved={({ latLng, formatted }) => {
          setCoords(latLng);
          setForm((f) => ({ ...f, address: formatted }));
        }}
        onError={() => toast.error("We couldn’t pin that address. Choose another suggestion.")}
      />
      {form.address && !coords && (
        <Muted>Pick a suggestion so we can capture the coordinates.</Muted>
      )}
      <Field
        placeholder="Recipient phone (e.g. 0700123123)"
        value={form.recipientPhone}
        type="tel"
        onChange={(e) =>
          setForm({ ...form, recipientPhone: e.target.value })
        }
      />
      <CheckRow>
        <input
          type="checkbox"
          checked={form.isPrimary}
          onChange={(e) => setForm({ ...form, isPrimary: e.target.checked })}
        />
        Set as my primary address
      </CheckRow>
      <FormRow>
        <PrimaryBtn type="button" disabled={!canSave || saving} onClick={handleSave}>
          {saving ? "Saving…" : editing === "new" ? "Save address" : "Update address"}
        </PrimaryBtn>
        <GhostBtn type="button" onClick={closeForm}>
          Cancel
        </GhostBtn>
      </FormRow>
    </FormCard>
  );

  return (
    <PageLayout
      icon={LocationOnIcon}
      title="Saved Addresses"
      subtitle="Manage your delivery addresses — reused everywhere you check out"
    >
      <Bar>
        {editing !== "new" && (
          <AddBtn type="button" onClick={openAdd}>
            + Add new address
          </AddBtn>
        )}
      </Bar>

      {editing === "new" && renderForm()}

      {error && <ErrorText>{error}</ErrorText>}

      {loading ? (
        <Muted>
          <BounceLoader size={28} color={ACCENT} />
        </Muted>
      ) : addresses.length === 0 && editing !== "new" ? (
        <Muted>
          You have no saved addresses yet. Add one and it will be available at
          checkout.
        </Muted>
      ) : (
        <List>
          {addresses.map((a) =>
            editing === a._id ? (
              <div key={a._id}>{renderForm()}</div>
            ) : (
              <Card key={a._id} $primary={a.isPrimary}>
                <CardTop>
                  <Label>{a.label || "Address"}</Label>
                  {a.isPrimary && (
                    <PrimaryBadge>
                      <StarIcon style={{ fontSize: 11 }} /> Primary
                    </PrimaryBadge>
                  )}
                  {!savedAddressLatLng(a) && <InvalidBadge>Update pin</InvalidBadge>}
                </CardTop>
                <AddressText>{a.address || a.description}</AddressText>
                {a.recipientPhone && <Phone>📞 {a.recipientPhone}</Phone>}
                <Actions>
                  <ActionBtn
                    type="button"
                    disabled={busyId === a._id}
                    onClick={() => openEdit(a)}
                  >
                    <EditIcon style={{ fontSize: 15 }} /> Edit
                  </ActionBtn>
                  {!a.isPrimary && (
                    <ActionBtn
                      type="button"
                      disabled={busyId === a._id || !savedAddressLatLng(a)}
                      onClick={() => handlePrimary(a)}
                    >
                      <StarIcon style={{ fontSize: 15 }} /> Set primary
                    </ActionBtn>
                  )}
                  <ActionBtn
                    type="button"
                    $danger
                    disabled={busyId === a._id}
                    onClick={() => handleDelete(a)}
                  >
                    <DeleteIcon style={{ fontSize: 15 }} /> Delete
                  </ActionBtn>
                </Actions>
              </Card>
            )
          )}
        </List>
      )}
    </PageLayout>
  );
};

export default SavedAddresses;
